import React from 'react';
import PropTypes from 'prop-types';
import { map, sortBy } from 'lodash';
import pluralize from 'pluralize';

import { Dropdown } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons';
import { faCaretDown } from '@fortawesome/pro-regular-svg-icons';

// NOT exported
const minRecordsForMerge = 2;
const maxRecordsForMerge = 5;

export default class ActionsDropdown extends React.Component {
  static propTypes = {
    aggregateView: PropTypes.bool,
    callLists: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      })
    ),
    callListsEnabled: PropTypes.bool,
    canAccessCrm: PropTypes.bool,
    canDelete: PropTypes.bool,
    canFindDuplicates: PropTypes.bool,
    canGroup: PropTypes.bool,
    canManageTags: PropTypes.bool,
    canTransferRecords: PropTypes.bool,
    canViewProjects: PropTypes.bool,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    groups: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      })
    ).isRequired,
    isCompany: PropTypes.bool,
    isPerson: PropTypes.bool,
    isProperty: PropTypes.bool,
    selectedGroupIds: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    ),
    selectedIds: PropTypes.array,
    onAddLeadsToProjects: PropTypes.func,
    onAddToCallList: PropTypes.func,
    onAddToGroup: PropTypes.func.isRequired,
    onBulkDelete: PropTypes.func,
    onBulkTagging: PropTypes.func.isRequired,
    onCopyRecords: PropTypes.func,
    onCreateCallList: PropTypes.func,
    onCreateGroup: PropTypes.func.isRequired,
    onCreateTasks: PropTypes.func,
    onDeleteFromGroup: PropTypes.func,
    onDeleteGroup: PropTypes.func,
    onEmailToGroup: PropTypes.func,
    onFindDuplicates: PropTypes.func,
    onMergeRecords: PropTypes.func,
    onMoveRecords: PropTypes.func,
    onSeeRelatedContacts: PropTypes.func,
    onUpdateGroup: PropTypes.func,
  };

  static defaultProps = {
    callLists: [],
    canViewProjects: false,
    disabled: false,
    selectedGroupIds: [],
  };

  static get MAX_RECORDS_FOR_MERGE() {
    return maxRecordsForMerge;
  }

  static get MIN_RECORDS_FOR_MERGE() {
    return minRecordsForMerge;
  }

  static MIN_RECORDS_FOR_COPY = 1;
  static MIN_RECORDS_FOR_MOVE = 1;

  render() {
    const { className, disabled, selectedIds } = this.props;

    const dropdownProps = { className };
    const dropdownToggleProps = { disabled };

    return (
      <Dropdown
        align={dropdownProps.align ? dropdownProps.align : 'end'}
        id="actions-dropdown"
        {...dropdownProps}
      >
        <Dropdown.Toggle variant="outline" {...dropdownToggleProps}>
          Actions
          <FontAwesomeIcon icon={faCaretDown} />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {this.renderGroupMenuSegment()}
          {this.renderFindDuplicateContacts()}
          {selectedIds.length > 0 && this.renderSelectedIdsOptions()}
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  renderFindDuplicateContacts() {
    const { canFindDuplicates, onFindDuplicates } = this.props;
    if (!canFindDuplicates) return null;

    return (
      <React.Fragment>
        <Dropdown.Item name="find-duplicates" onClick={onFindDuplicates}>
          Find Duplicates
        </Dropdown.Item>
      </React.Fragment>
    );
  }

  renderGroupMenuSegment() {
    const {
      aggregateView,
      canGroup,
      selectedGroupIds,
      onDeleteGroup,
      onUpdateGroup,
      groups,
      selectedIds,
    } = this.props;
    const groupLabel =
      selectedGroupIds && selectedGroupIds.length === 1 ? 'Group' : 'Groups';
    const showGroupMenu =
      selectedGroupIds.length > 0 && selectedGroupIds[0] !== 'ungrouped';
    const showGroupOptions = selectedIds.length > 0;
    const showAddGroupSection =
      groups && groups.length > 0 && selectedIds.length > 0;
    const showDeleteFromGroupSection = groups && groups.length > 0 && selectedIds.length > 0;
    const anyItemsShown =
      showGroupMenu ||
      showGroupOptions ||
      showAddGroupSection ||
      showDeleteFromGroupSection;

    if (!canGroup || aggregateView || !anyItemsShown) return null;

    return (
      <React.Fragment>
        {showGroupMenu && (
          <React.Fragment>
            <Dropdown.Item name="delete-group" onClick={onDeleteGroup}>
              Delete {groupLabel}
            </Dropdown.Item>
            <Dropdown.Item name="rename-group" onClick={onUpdateGroup}>
              Rename {groupLabel}
            </Dropdown.Item>
            <Dropdown.Divider />
          </React.Fragment>
        )}
        {showGroupOptions && this.renderGroupOptions()}
        {showAddGroupSection && this.renderAddToGroupSection()}
        {showDeleteFromGroupSection && this.renderDeleteFromGroupSection()}
        {(showGroupOptions || showAddGroupSection || showDeleteFromGroupSection) && (
          <Dropdown.Divider />
        )}
      </React.Fragment>
    );
  }

  renderSelectedIdsOptions() {
    const {
      aggregateView,
      callListsEnabled,
      canAccessCrm,
      canDelete,
      canManageTags,
      canTransferRecords,
      isCompany,
      isPerson,
      isProperty,
      selectedIds,
      onBulkDelete,
      onBulkTagging,
      onCopyRecords,
      onMergeRecords,
      onMoveRecords,
      onSeeRelatedContacts,
    } = this.props;

    const canMerge =
      !aggregateView &&
      onMergeRecords &&
      selectedIds.length >= ActionsDropdown.MIN_RECORDS_FOR_MERGE &&
      selectedIds.length <= ActionsDropdown.MAX_RECORDS_FOR_MERGE;

    const canCopy =
      isProperty &&
      !aggregateView &&
      onCopyRecords &&
      selectedIds.length >= ActionsDropdown.MIN_RECORDS_FOR_COPY;
    const canMove =
      isProperty &&
      !aggregateView &&
      onMoveRecords &&
      selectedIds.length >= ActionsDropdown.MIN_RECORDS_FOR_MOVE;
    const canPerformDatabaseAction = canCopy || canMove;
    const showRelatedContacts = (isProperty || isCompany) && !aggregateView;
    const showCallListOptions = canAccessCrm && callListsEnabled && !isProperty;
    const showTagging = onBulkTagging && canManageTags;
    const showDelete = onBulkDelete && canDelete;
    const anyVisible = isPerson || canMerge || canPerformDatabaseAction || showRelatedContacts || showCallListOptions ||
      showTagging || showDelete;

    return (
      <React.Fragment>
        {anyVisible && <Dropdown.Divider />}
        {showRelatedContacts && (
          <React.Fragment>
            <Dropdown.Item
              key="related-contacts"
              onClick={onSeeRelatedContacts}
            >
              See Related Contacts
            </Dropdown.Item>
            <Dropdown.Divider />
          </React.Fragment>
        )}
        {isPerson && this.renderPeopleOptions()}
        {showCallListOptions && this.renderCallListOptions()}
        {canMerge && (
          <Dropdown.Item
            key="merge-records"
            name="merge"
            onClick={onMergeRecords}
          >
            Merge
          </Dropdown.Item>
        )}
        {showTagging && (
          <Dropdown.Item
            key="bulk-tagging"
            name="bulk-tagging"
            onClick={onBulkTagging}
          >
            Tag
          </Dropdown.Item>
        )}
        {showDelete && (
          <Dropdown.Item
            key="bulk-delete"
            name="bulk-delete"
            onClick={onBulkDelete}
          >
            Delete
          </Dropdown.Item>
        )}
        {canPerformDatabaseAction && (
          <React.Fragment>
            <Dropdown.Divider />
            {canMove && (
              <Dropdown.Item
                disabled={!canTransferRecords}
                key="move-records"
                name="move-records"
                onClick={canTransferRecords ? onMoveRecords : null}
              >
                Move to Another Database
              </Dropdown.Item>
            )}
            {canCopy && (
              <Dropdown.Item
                disabled={!canTransferRecords}
                key="copy-records"
                name="copy-records"
                onClick={canTransferRecords ? onCopyRecords : null}
              >
                Copy to Another Database
              </Dropdown.Item>
            )}
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  renderPeopleOptions() {
    const {
      canAccessCrm,
      canViewProjects,
      selectedIds,
      onAddLeadsToProjects,
      onCreateTasks,
      onEmailToGroup,
    } = this.props;

    return (
      <React.Fragment>
        {canAccessCrm && (
          <React.Fragment>
            <Dropdown.Item name="tasks-group" onClick={onCreateTasks}>
              Create {pluralize('Task', selectedIds.length)}
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item name="email-group" onClick={onEmailToGroup}>
              Email Contacts
            </Dropdown.Item>
            <Dropdown.Divider />
          </React.Fragment>
        )}
        {canViewProjects && (
          <React.Fragment>
            <Dropdown.Item key="projects-group" onClick={onAddLeadsToProjects}>
              Add Leads to Project
            </Dropdown.Item>
            <Dropdown.Divider />
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  renderCallListOptions() {
    const { callLists, callListsEnabled, onAddToCallList, onCreateCallList } =
      this.props;
    if (!callListsEnabled) return null;

    const sortedCallLists = sortBy(callLists, list => list.name);
    const listOptions = map(sortedCallLists, list => (
      <Dropdown.Item key={list.id} onClick={() => onAddToCallList(list)}>
        {list.name}
      </Dropdown.Item>
    ));

    const showAddLink = callLists.length > 0;

    return (
      <React.Fragment>
        {onCreateCallList && (
          <li>
            <Dropdown.Item name="create-call-list" onClick={onCreateCallList}>
              Create Call List
            </Dropdown.Item>
          </li>
        )}
        {showAddLink && (
          <li name="add-to-call-list">
            <Dropdown>
              <Dropdown.Toggle as="a" className="dropdown-item py-2 px-3">
                Add to Call List
                <FontAwesomeIcon
                  className="ms-auto"
                  icon={faAngleRight}
                />
              </Dropdown.Toggle>
              <Dropdown.Menu>{listOptions}</Dropdown.Menu>
            </Dropdown>
          </li>
        )}
        <Dropdown.Divider />
      </React.Fragment>
    );
  }

  renderGroupOptions() {
    return (
      <Dropdown.Item name="create-group" onClick={this.props.onCreateGroup}>
        Create Group
      </Dropdown.Item>
    );
  }

  renderAddToGroupSection() {
    const { groups, onAddToGroup } = this.props;
    const groupOptions = map(groups, group => (
      <li>
        <Dropdown.Item key={group.id} onClick={() => onAddToGroup(group)}>
          {group.name}
        </Dropdown.Item>
      </li>
    ));

    return (
      <React.Fragment>
        <li name="add-to-group">
          <Dropdown>
            <Dropdown.Toggle as="a" className="dropdown-item py-2 px-3">
              Add to Group
              <FontAwesomeIcon
                className="ms-auto"
                icon={faAngleRight}
              />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {groupOptions}
            </Dropdown.Menu>
          </Dropdown>
        </li>
      </React.Fragment>
    );
  }

  renderDeleteGroupOptions() {
    const {
      groups,
      isProperty,
      isCompany,
      selectedIds,
      selectedGroupIds,
      onDeleteFromGroup,
    } = this.props;

    const selectedGroups =
      selectedGroupIds.length > 0
        ? groups.filter(group => selectedGroupIds.includes(group.id))
        : groups;

    return selectedGroups.map((group) => {
      let ids = [];
      if (isProperty) {
        ids = group.propertyIds;
      } else if (isCompany) {
        ids = group.companyIds;
      } else {
        ids = group.personIds;
      }

      if (ids.some(id => selectedIds.includes(id))) {
        return (
          <Dropdown.Item
            key={group.id}
            onClick={() => onDeleteFromGroup(group)}
          >
            {group.name}
          </Dropdown.Item>
        );
      }
    });
  }

  renderDeleteFromGroupSection() {
    const groupOptions = this.renderDeleteGroupOptions();
    if (groupOptions.filter(Boolean).length > 0) {
      return (
        <React.Fragment>
          <li name="delete-from-group">
            <Dropdown>
              <Dropdown.Toggle as="a" className="dropdown-item">
                Delete from Group
                <FontAwesomeIcon
                  className="ms-auto"
                  icon={faAngleRight}
                />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {groupOptions}
              </Dropdown.Menu>
            </Dropdown>
          </li>
        </React.Fragment>
      );
    }
  }
}
